import Backbone from 'backbone';
import utils from 'lib/utils';
import LocaleService from 'services/loader-handler-services/locale-service';
import CourseSettingsService from 'services/course-handler-services/course-settings-service';
export default class AudioServiceBase {
  constructor(view) {
    Object.assign(this, Backbone.Events);
    this.audioView = view; //This has to initialize with model view so that we attach events
    this.playlist = []; // Array of sound objects
    this.language = LocaleService.getLanguage();
  }

  retryLogic(url, params) {
    const id = this.getIdFromPath(url);
    this.language = 'en';
    const newUrl = utils.coursePath(`audio/${this.language}/${id}.mp3`);
    return { newUrl, newParams: params };
  }

  async loadAudio(sources) {
    if (sources?.length && Array.isArray(sources)) {
      const allSources = await Promise.all(
        sources.map((source, index) => {
          source.path = this.getCoursePath(source.path);
          const id = this.getIdFromPath(source.path);
          if (id) {
            // uses the last source path as the id. sources could have different file names
            // but this is good enough for use as an id in the events
            this.audioView.model.set('id', id);
          }
          return utils
            .fetch(source.path, {}, { count: 1, callback: this.retryLogic.bind(this) })
            .then(async response => {
              if (response.status !== 200) {
                return Promise.reject(response);
              }
              /**
               *  Here we are updating the index because howler service plays the audio in incremental order.
               *  Also we wanted to exclude the audios that were not resolved.
               */
              source.index = index;

              /**
               * Response body interface methods can be used once.
               * Using them twice throws a TypeError indicating that the stream has already been read.
               * Therefore, allow multiple uses of response.
               */

              const responseClone = response.clone();

              /**
               * reads stream to completion and resolves with a usvstring
               * (first use of response body interface method)
               */

              const responseText = await response.text();
              // For git LFSd files, they will have header with content type application/vnd.git-lfs+json
              if (response.headers.get('Content-Type') === 'application/vnd.git-lfs+json') {
                this.setLFSsrc(responseText, source);
              } else {
                /**
                 * For some audio, for some cleanup reason, it seems response headers return audio/mpeg content-type
                 * even though under the hood the files are Git LFS file. Therefore,
                 * ensure that the file is a git lfs file
                 */

                const isLFSFile = responseText?.includes('https://git-lfs.github.com/spec/v1');

                isLFSFile ? this.setLFSsrc(responseText, source) : this.setBlobSrc(await responseClone.blob(), source); // reads stream to completion and resolves with a Blob (second use of response body interface method)
              }

              source.id = id;
              source.isReady = true;

              this.howls = [
                id,
                source.index,
                source.path,
                !!source.autoplay,
                !!(source.selector || source.on || source.component),
                source.playOrder ?? false,
                source.selector || source.on || source.component,
              ];
              return Promise.resolve(source);
            })
            .catch(error => {
              this.audioView.readyToGo();
              console.error(`Could not load audio: ${id}`, error); //eslint-disable-line no-console
            });
        })
      );

      return allSources.filter(source => source);
    }
  }

  /**
   * Build sound object and add it to playlist ({id, src, howl}).
   * by default howl is null until we can attach events
   * @param {Array} source Array of audio id and source path
   */
  set howls(source) {
    const [id, index, path, autoplay, trigger, order, eventTarget] = source;
    this.playlist.push({
      id,
      index,
      src: path,
      autoplay: autoplay,
      howl: null,
      hasTrigger: trigger,
      playOrder: order,
      eventTarget: eventTarget,
    });
  }

  getOID(lfs) {
    const lfsData = lfs.split('\n');
    return lfsData[1].split(' ')[1].replace('sha256:', '');
  }

  setBlobSrc(blob, src) {
    src.path = URL.createObjectURL(blob);
  }

  setLFSsrc(lfs, src) {
    const oidSha = this.getOID(lfs);
    const name = CourseSettingsService.get('name');
    const language = this.language;
    const basePath = CourseSettingsService.get('media_host');

    src.path = `${basePath}/curriculums/${name}/media/audio/${language}/${oidSha}.mp3`;
    src.isReady = true;
  }

  getCoursePath(path) {
    return utils.coursePath(`audio/${LocaleService.getLanguage()}/${path}`);
  }

  getIdFromPath(path) {
    return path.split('/').pop().split('.').shift();
  }

  formatTime(secs) {
    const minutes = Math.floor(secs / 60) || 0;
    const seconds = secs - minutes * 60 || 0;

    return `${minutes}:${(seconds < 10 ? '0' : '') + seconds}`;
  }
}
